// @ts-strict-ignore
import { Component, Input, OnInit } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { ProjectOrderingUtils } from '@project/components/project/util/project-ordering-utils';
import { AlertHelper } from '@shared/alert/alert.helper';
import { AlertButton } from '@shared/alert/alertButton';
import { StringUtils } from '@shared/util/string-utils';
import { FileUtils } from '@shared/util/file-utils';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { HtmlUtils } from '@shared/util/html-utils';
import { ProjectLinkAccessVO } from '../../../model/vo/project-link-access-vo';
import { DatePipe } from '@angular/common';
import { ProjectDataService } from '../../../service/project-data.service';
import { ProjectService } from '../../import-export/service/project.service';
import { ProjectComponentPresenter } from '../../project-component.presenter';
import { UserUtils } from '@user/service/user-utils';
import { UserModel } from '@user/model/user.model';

export enum ShareLinkType {
  TEMPORARY,
  PERMANENT,
}

@Component({
  standalone: false,
  templateUrl: './share-project-link.component.html',
  providers: [DatePipe],
  selector: 'app-share-project-link',
})
export class ShareProjectLinkComponent implements OnInit {
  public readonly linkTypes: ShareLinkType[] = [ShareLinkType.TEMPORARY, ShareLinkType.PERMANENT];

  @Input() projectId: number;
  @Input() projectVersion: number;
  @Input() modificationDateServerTimeStamp: string;
  @Input() articleNumberLong: string;
  @Input() projectName: string;

  public shareLinkType: ShareLinkType = this.linkTypes[0];
  public temporaryLink: string = '';

  private projectLinkAccess: ProjectLinkAccessVO;
  private prevLocationUrl: string;

  public isProjectSharingAvailable: boolean = false;

  public get temporaryLinkValidDays() {
    return this.projectLinkAccess ? this.projectLinkAccess.validDays : 0;
  }

  constructor(
    private bsModalRef: BsModalRef,
    private alertHelper: AlertHelper,
    private localeResourceManager: LocaleResourceManager,
    private projectDataService: ProjectDataService,
    private projectService: ProjectService,
    private projectComponentPresenter: ProjectComponentPresenter,
    private userModel: UserModel,
  ) {}

  public ngOnInit(): void {
    this.projectDataService.createProjectLinkAccess(this.projectId, this.projectVersion).subscribe({
      next: (data) => {
        this.projectLinkAccess = data;
        this.generateTemporaryLink();
        this.changeUrlLocation();
        },
      error: () => this.close()
    });
    this.isProjectSharingAvailable = this.userModel.user?.hasPermission("GET_LIST_OF_FRIENDS")
      && UserUtils.canHaveFriend(this.userModel.user);
  }

  public close(): void {
    this.changeUrlLocation(true);
    this.bsModalRef.hide();
  }

  public copyTemporaryLink(): void {
    this.copyProjectUrl();
    this.close();
  }

  public openPermissionDialog(): void {
    this.projectComponentPresenter.openPermissionDialog();
  }

  public generateProjectUrlHtmlFile(): void {
    const link = this.getLinkByType(this.shareLinkType);
    const htmlContent: string = `<HTML>\n<HEAD>\n<meta http-equiv="refresh" content="0; URL=${link}">\n</HEAD>\n</HTML>`;

    let fileName: string = null;
    if (this.articleNumberLong) {
      fileName = this.articleNumberLong;
    }
    if (StringUtils.isEmpty(fileName)) {
      fileName = this.projectName || this.projectService.getDefaultProjectName();
    }
    FileUtils.saveTextFile(StringUtils.sanitizeFileName(fileName), htmlContent, '.html', false);
    this.close();
  }

  private copyProjectUrl(linkType: ShareLinkType = ShareLinkType.TEMPORARY): void {
    const link = this.getLinkByType(linkType);
    HtmlUtils.copyTextToClipboard(link);
    this.alertHelper.showAlert(
      'main.menu.message.confirm_copy_project_url',
      `${this.localeResourceManager.getValueByKey('main.menu.message.copy_project_url')}\n${link}`,
      null,
      [AlertButton.OK],
      "info"
    );
  }

  private getLinkByType(linkType: ShareLinkType): string {
    if (linkType === ShareLinkType.TEMPORARY) {
      return this.temporaryLink;
    }
    return ProjectOrderingUtils.prepareDirectLinkByProjectIdAndSaveDateWithFallbackToProjectVersion(
      this.projectId,
      this.modificationDateServerTimeStamp,
      this.projectVersion
    );
  }

  private generateTemporaryLink(): void {
    this.temporaryLink = ProjectOrderingUtils.prepareProjectTemporaryLink(
      this.projectId,
      this.modificationDateServerTimeStamp,
      this.projectVersion,
      this.projectLinkAccess.guid
    );
  }

  private changeUrlLocation(rollBack?: boolean): void {
    if (rollBack && this.prevLocationUrl) {
      if (window.location.href !== this.prevLocationUrl) {
        history.replaceState(history.state, null, this.prevLocationUrl);
      }
      return;
    }
    this.prevLocationUrl = window.location.href;
    if (!isNaN(this.projectId)) {
      const link: string = this.getLinkByType(ShareLinkType.PERMANENT);
      history.replaceState(history.state, null, link);
    }
  }
}
